import { useCallback, useEffect, useState, useSyncExternalStore } from 'react'

/*
 * Captures the browser's "install this app" prompt so it can be offered
 * from our own buttons. Listeners are attached as soon as this module loads
 * in the browser, because the prompt can fire before the page is
 * interactive and would otherwise be missed.
 */

type BeforeInstallPromptEvent = Event & {
    prompt: () => Promise<void>
    userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>
}

let deferredPrompt: BeforeInstallPromptEvent | null = null
let installedThisSession = false
const listeners = new Set<() => void>()

const notify = () => listeners.forEach(listener => listener())

if (typeof window !== 'undefined') {
    window.addEventListener('beforeinstallprompt', event => {
        event.preventDefault()
        deferredPrompt = event as BeforeInstallPromptEvent
        notify()
    })

    window.addEventListener('appinstalled', () => {
        deferredPrompt = null
        installedThisSession = true
        notify()
    })
}

const subscribe = (listener: () => void) => {
    listeners.add(listener)
    return () => {
        listeners.delete(listener)
    }
}

export type InstallState = 'unknown' | 'installed' | 'available' | 'ios' | 'manual'

export const useInstallPrompt = () => {
    const canPrompt = useSyncExternalStore(subscribe, () => Boolean(deferredPrompt), () => false)
    const installed = useSyncExternalStore(subscribe, () => installedThisSession, () => false)
    const [platform, setPlatform] = useState<{ standalone: boolean, ios: boolean } | null>(null)

    useEffect(() => {
        const nav = window.navigator as Navigator & { standalone?: boolean }
        const standalone = window.matchMedia('(display-mode: standalone)').matches || nav.standalone === true
        const ios = /iphone|ipad|ipod/i.test(nav.userAgent) || (nav.platform === 'MacIntel' && nav.maxTouchPoints > 1)

        setPlatform({ standalone, ios })
    }, [])

    let state: InstallState = 'unknown'

    if (platform) {
        if (platform.standalone || installed) state = 'installed'
        else if (canPrompt) state = 'available'
        else if (platform.ios) state = 'ios'
        else state = 'manual'
    }

    const install = useCallback(async (): Promise<boolean> => {
        const prompt = deferredPrompt
        if (!prompt) return false

        await prompt.prompt()
        const choice = await prompt.userChoice

        deferredPrompt = null
        notify()

        return choice.outcome === 'accepted'
    }, [])

    return { state, install }
}
